import { X } from 'lucide-react';
import { Toaster as Sonner } from 'sonner';
import { useTheme } from '@/components/theme/useTheme';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

type ToasterProps = React.ComponentProps<typeof Sonner>;

/**
 * Sonner's surface palette, re-pointed at the app's tokens (14ykpqtv2na).
 *
 * Inline on Sonner's `<ol data-sonner-toaster>`, so it beats Sonner's own `[data-sonner-theme]` palette at any
 * specificity, and it follows the theme class on `<html>` the moment it changes. `popover`, not `background`: a toast
 * floats over the page like a popover, and in Industrial `--background` is the grey canvas a toast would melt into.
 */
const SURFACE = {
  '--normal-bg': 'hsl(var(--popover))',
  '--normal-text': 'hsl(var(--popover-foreground))',
  '--normal-border': 'hsl(var(--border))',
  '--normal-bg-hover': 'hsl(var(--muted))',
  '--normal-border-hover': 'hsl(var(--border))',
} as React.CSSProperties;

/**
 * The app's one toaster, mounted in `App.tsx`.
 *
 * ⚠️ Every class below carries `!`. Sonner injects its stylesheet into `<head>` at runtime, so in a production build
 * it lands AFTER ours and wins every specificity tie — while under `npm run dev` it lands BEFORE (`main.tsx` imports
 * `App` before `globals.css`) and loses them. A class that ties looks right in dev and silently does nothing in prod;
 * that is how this file's old `group-[.toast]:` classes never reached a user. Check toasts on `npm run preview`.
 */
export function Toaster({ ...props }: ToasterProps) {
  const { resolvedTheme } = useTheme();

  return (
    // A click on a toast belongs to the toast (14ykpqtv2gv). Radix's modal layers spot an outside click with a
    // `pointerdown` listener on `document`; stopping the bubble here keeps a toast's X from closing the dialog under it.
    <div className="contents" onPointerDown={(e) => e.stopPropagation()}>
      <Sonner
        position="top-right"
        // Industrial is a light theme. Never 'system': DOP resolves System itself, and Sonner would follow the OS.
        theme={resolvedTheme === 'dark' ? 'dark' : 'light'}
        closeButton
        // A modal dialog sets `body { pointer-events: none }`; without this, toasts are click-through while one is open
        // and the click lands on the dialog's overlay.
        className="pointer-events-auto"
        style={SURFACE}
        icons={{ close: <DismissIcon /> }}
        toastOptions={{
          closeButtonAriaLabel: 'Dismiss',
          classNames: {
            description: '!text-muted-foreground',
            actionButton: '!bg-primary !text-primary-foreground',
            cancelButton: '!bg-muted !text-muted-foreground',
            // Sonner draws a 20px X; its rule outranks a plain `h-6`. No colour classes: Sonner paints the X from the
            // same variables as the card it sits on.
            closeButton: '!h-6 !w-6',
            // Sonner puts `classNames[type]` on the toast and colours no icon itself, so this tints the icon only;
            // the card stays plain (owner, 2026-10-14). `richColors` would tint the whole card.
            success: '[&_[data-icon]]:!text-success',
            warning: '[&_[data-icon]]:!text-warning',
            error: '[&_[data-icon]]:!text-destructive',
            info: '[&_[data-icon]]:!text-info',
          },
        }}
        {...props}
      />
    </div>
  );
}

/**
 * The X's glyph and its hover label. Sonner owns the `<button>` (its accessible name is `closeButtonAriaLabel`), so the
 * tooltip can only wrap what is inside it — which means it shows on hover, not on keyboard focus.
 *
 * There is no app-wide `TooltipProvider`, so this brings its own. `side="left"`: the toaster sits above `z-50`, so a
 * tooltip that flipped over the toast would draw behind it.
 */
function DismissIcon() {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="flex h-full w-full items-center justify-center">
            <X className="h-3.5 w-3.5" aria-hidden />
          </span>
        </TooltipTrigger>
        <TooltipContent side="left">Dismiss</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
